import { useMemo } from 'react';
import { useTable } from 'spacetimedb/react';
import type { Identity, Timestamp } from 'spacetimedb';
import { tables as minetwinTables } from '@sdk/minetwin';

/**
 * The configured onboarding agent per project, sourced from the
 * `visible_project_agents` view (admin-scoped; OrgAdmin sees every
 * project's mapping). Powers the Site Settings agent picker so the
 * coordinator can see which agent the server will fall through to
 * when `assign_orientation` checks `project_agent`.
 *
 * Use `byProjectId(projectId)` to read the single row for the
 * currently-selected site — this is what the picker pre-selects from.
 */
export type ProjectAgentRow = {
  projectId: string;
  /** Identity-typed since Samuel's 2026-06-17 bindings regen.
   *  `.toHexString()` for stable string keys / display. */
  agentIdentity: Identity;
  displayName: string;
  updatedAt: Timestamp;
};

export function useProjectAgents(): {
  agents: ProjectAgentRow[];
  byProjectId: (projectId: string) => ProjectAgentRow | null;
  isReady: boolean;
} {
  const [rows, isReady] = useTable(
    'minetwin',
    minetwinTables.visible_project_agents,
  );

  const agents = useMemo<ProjectAgentRow[]>(() => {
    return rows.map((r) => ({
      projectId: r.projectId,
      agentIdentity: r.agentIdentity,
      displayName: r.displayName,
      updatedAt: r.updatedAt,
    }));
  }, [rows]);

  const byProjectId = useMemo(() => {
    const map = new Map<string, ProjectAgentRow>();
    for (const a of agents) map.set(a.projectId, a);
    return (projectId: string) => map.get(projectId) ?? null;
  }, [agents]);

  return { agents, byProjectId, isReady };
}
